import { drawGrid, drawPosition } from './canvasDrawing';

/**
 * Lance l'animation de transition vers la prochaine période
 */
export function lancerAnimationVersProchainePeriode({
  canvasRef,
  currentSession,
  periods,
  currentTime,
  canvasPixelWidth,
  canvasPixelHeight,
  animationFrameRef,
  isPlayingRef,
  audioRef,
  setCurrentTime,
  selectedPositionId,
  dancers,
  gridColumns = 28,
  gridRows = 18,
}) {
  // console.log("Lancement de l'animation vers la prochaine période depuis le temps ", currentTime);

  if (!canvasRef.current || !currentSession || !periods || periods.length === 0) return;

  const ctx = canvasRef.current.getContext('2d');
  const firstPeriod = periods[0];
  const lastPeriod = [...periods].reverse().find(period => period.end_time !== null);

  // ✅ Cas spécial : avant la première période
  if (!firstPeriod.start_time || currentTime < firstPeriod.start_time) {
    function drawStaticFrame() {
      if (!canvasRef.current || !isPlayingRef.current) return;
      
      // ✅ Vérifier si on a atteint le début de la première période
      if (audioRef.current && audioRef.current.currentTime >= firstPeriod.start_time) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
        return;
      }
      
      ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
      drawGrid(ctx, canvasPixelWidth, canvasPixelHeight, periods, gridColumns, gridRows);
      
      ctx.save();
      ctx.globalAlpha = 0.5;
      firstPeriod.positions?.forEach(pos => {
        drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers);
      });
      ctx.restore();
      
      animationFrameRef.current = requestAnimationFrame(drawStaticFrame);
    }
    animationFrameRef.current = requestAnimationFrame(drawStaticFrame);
    return;
  }

  // ✅ Cas spécial : après la dernière période
  if (lastPeriod && currentTime > lastPeriod.end_time) {
    function drawStaticFrame() {
      if (!canvasRef.current || !isPlayingRef.current) return;
      
      ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
      drawGrid(ctx, canvasPixelWidth, canvasPixelHeight, periods, gridColumns, gridRows);
      
      ctx.save();
      ctx.globalAlpha = 0.5;
      lastPeriod.positions?.forEach(pos => {
        drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers);
      });
      ctx.restore();
      
      animationFrameRef.current = requestAnimationFrame(drawStaticFrame);
    }
    animationFrameRef.current = requestAnimationFrame(drawStaticFrame);
    return;
  }

  // Cas normal : entre deux périodes
  let previousPeriod = null;
  let nextPeriod = null;
  for (let i = 0; i < periods.length; i++) {
    if (currentTime < periods[i].start_time) {
      nextPeriod = periods[i];
      previousPeriod = periods[i - 1] || firstPeriod;
      break;
    }
  }
  if (!previousPeriod || !nextPeriod) return;

  const gapDuration = nextPeriod.start_time - previousPeriod.end_time;
  let startFactor = (currentTime - previousPeriod.end_time) / gapDuration;
  startFactor = Math.max(0, Math.min(startFactor, 1));

  const remainingDurationMs = (1 - startFactor) * gapDuration * 1000;
  const startTimeMs = performance.now();

  const animatedPositions = [];
  const staticPositions = [];
  previousPeriod.positions?.forEach(prevPos => {
    if (!prevPos.dancer_id) {
      staticPositions.push(prevPos);
    } else {
      const nextPos = nextPeriod.positions?.find(pos => pos.dancer_id === prevPos.dancer_id);
      if (nextPos) {
        animatedPositions.push({ prevPos, nextPos });
      } else {
        staticPositions.push(prevPos);
      }
    }
  });

  function animateFrame(now) {
    if (!canvasRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    if (!isPlayingRef.current) {
      return;
    }

    const elapsed = now - startTimeMs;
    const elapsedRatio = Math.min(elapsed / remainingDurationMs, 1);
    const factor = startFactor + (1 - startFactor) * elapsedRatio;

    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasPixelWidth, canvasPixelHeight);
    drawGrid(ctx, canvasPixelWidth, canvasPixelHeight, periods, gridColumns, gridRows);

    animatedPositions.forEach(({ prevPos, nextPos }) => {
      const currentX = prevPos.x + factor * (nextPos.x - prevPos.x);
      const currentY = prevPos.y + factor * (nextPos.y - prevPos.y);
      const tempPos = {
        x: currentX,
        y: currentY,
        dancer_id: prevPos.dancer_id,
        id: `anim-${prevPos.dancer_id}`,
      };
      drawPosition(ctx, tempPos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers);
    });

    ctx.save();
    ctx.globalAlpha = 0.5;
    staticPositions.forEach(pos => 
      drawPosition(ctx, pos, canvasPixelWidth, canvasPixelHeight, selectedPositionId, dancers)
    );
    ctx.restore();

    if (elapsedRatio < 1) {
      animationFrameRef.current = requestAnimationFrame(animateFrame);
    } else {
      // ✅ Nettoyer la référence quand l'animation est terminée
      animationFrameRef.current = null;
      if (audioRef.current) {
        setCurrentTime(nextPeriod.start_time);
      }
    }
  }

  animationFrameRef.current = requestAnimationFrame(animateFrame);
}

/**
 * Stoppe l'animation en cours
 */
export function stopAnimation(animationFrameRef) {
  if (animationFrameRef.current) {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = null;
  }
}
